import { type ReactNode, type ChangeEvent, type FC, useCallback } from 'react';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { AnimatePresence } from 'motion/react';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { ExportCsvButton } from '@/shared/components/ExportCSVButton';
import QuestionMarkTooltip, {
  type QuestionMarkTooltipProps,
} from '@/shared/components/QuestionMarkTooltip';
import { type PaginationControls } from '@/shared/hooks/useRemotePagination';
import DoubleArrowIcon from '@/shared/icons/DoubleArrow';
import Chevron from '@/shared/icons/flip-ui-kit/large/Chevron';

const paginationButtonStyles =
  'text-white bg-cf-gray-3 rounded-[6px] border border-cf-gray-4 px-2 py-1 cursor-pointer hover:bg-cf-gray-4 hover:border-cf-gray-4';
const disabledStyles =
  'text-cf-gray-5 border-cf-gray-3 hover:bg-cf-gray-3 hover:border-cf-gray-3 hover:cursor-default';

const PageCounter = ({
  paginationControls: props,
  showTotalPages,
}: {
  paginationControls: PaginationControls;
  showTotalPages: boolean;
}) => (
  <div className="flex flex-row items-center">
    <button
      suppressHydrationWarning
      disabled={
        props.useCursor ? !props.paginationCursor.hasPreviousPage : props.pageCounter.atBeginning
      }
      type="button"
      className="p-2 text-white disabled:text-cf-light-3"
      onClick={props.useCursor ? props.paginationCursor.previous : props.pageCounter.decrement}
    >
      <Chevron flip orientation="left-right" />
    </button>
    <p suppressHydrationWarning className="mx-0 text-right text-14 md:mx-4">
      {props.useCursor ? (
        <>
          Page {props.paginationCursor.pageNumber}{' '}
          {showTotalPages && <>of {props.pageCounter.end}</>}
        </>
      ) : (
        <>
          Page {props.pageCounter.current} {showTotalPages && <>of {props.pageCounter.end}</>}
        </>
      )}
    </p>
    <button
      suppressHydrationWarning
      disabled={props.useCursor ? !props.paginationCursor.hasNextPage : props.pageCounter.atEnd}
      type="button"
      className="p-2 text-white disabled:text-cf-light-3"
      onClick={props.useCursor ? props.paginationCursor.next : props.pageCounter.increment}
    >
      <Chevron orientation="left-right" />
    </button>
  </div>
);

type Alignment = 'left' | 'center' | 'right';

export type Column =
  | string
  | {
      name: string;
      alignment?: Alignment;
      sortable?: boolean;
      /** each column needs a unique name for the React key, but we don't always want to show the column name */
      labelHidden?: boolean;
      tooltip?: QuestionMarkTooltipProps['content'];
      /** rendered immediately before the column name, e.g. to badge a metric as protocol-specific */
      icon?: ReactNode;
    };

type TableV2Props = {
  columns: Column[];
  rows: React.JSX.Element[] | null;
  isLoading?: boolean | undefined;
  sorting?: Sort;
  paginationControls?: PaginationControls;
  emptyRowsContent?: ReactNode;
  showTotalPages?: boolean;
  hideHeaders?: boolean;
  className?: string;
  thClassName?: string;
  tbodyClassName?: string;
  scrollClassName?: string;
  exportCsv?: () => void;
};

export type Sort = {
  setCol: (index: number) => void;
  sort: {
    col: number;
    direction: 'ASC' | 'DESC';
  };
};

export const TableV2: FC<TableV2Props> = ({
  columns,
  rows,
  isLoading,
  paginationControls,
  sorting,
  className,
  thClassName,
  tbodyClassName,
  scrollClassName,
  emptyRowsContent = 'No activity here yet',
  hideHeaders = false,
  showTotalPages = true,
  exportCsv,
}) => {
  const setSort = useCallback(
    (colIndex: number) => {
      paginationControls?.pageCounter.set(1);
      sorting?.setCol(colIndex);
    },
    [paginationControls, sorting],
  );

  return (
    <div className="flex flex-col space-y-8">
      <div className={classNames('h-full w-full overflow-x-auto', isLoading && 'bg-cf-gray-3')}>
        <MacScrollbar skin="dark" className={scrollClassName}>
          <table
            style={{
              borderCollapse: 'inherit',
            }}
            className={classNames(
              'h-full w-full border-spacing-0 rounded-md border border-cf-gray-3-5 bg-cf-gray-3 group-[.cf-is-panel]:rounded-t-none',
              className,
            )}
          >
            {!hideHeaders && (
              <thead className="text-14 text-cf-light-2">
                <tr>
                  {columns.map((column, index) => {
                    let name: string | React.JSX.Element;
                    let key: string;
                    let alignment: Alignment;
                    let sortable: boolean;
                    let tooltip: ReactNode;
                    let icon: ReactNode;

                    if (typeof column === 'string') {
                      name = column;
                      key = column;
                      alignment = 'left';
                      sortable = Boolean(sorting);
                    } else {
                      name = column.labelHidden ? '' : column.name;
                      key = column.name;
                      alignment = column.alignment ?? 'left';
                      sortable = column.sortable ?? Boolean(sorting);
                      tooltip = column.tooltip;
                      icon = column.icon;
                    }

                    return (
                      <th
                        key={key}
                        className={classNames(
                          'text-left',
                          sortable && 'cursor-pointer',
                          thClassName,
                          'whitespace-nowrap border-b border-cf-gray-3-5 p-4 font-aeonikRegular font-normal',
                        )}
                        onClick={() => sortable && setSort(index)}
                      >
                        <span
                          className={classNames(
                            'flex items-center gap-x-1',
                            alignment === 'left' && 'justify-start',
                            alignment === 'center' && 'justify-center',
                            alignment === 'right' && 'justify-end',
                          )}
                        >
                          {icon}
                          {name}
                          {tooltip && <QuestionMarkTooltip content={tooltip} />}
                          {sortable && (
                            <DoubleArrowIcon
                              className="text-cf-light-1"
                              activeColor="white"
                              direction={
                                sorting?.sort?.col === index ? sorting.sort.direction : undefined
                              }
                            />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody
              className={classNames(
                'text-14 text-cf-light-3 [&>tr:not(:last-child)>td]:border-b [&>tr>td]:border-cf-gray-3-5 [&>tr>td]:p-4',
                tbodyClassName,
              )}
            >
              {isLoading && !paginationControls && (
                <tr>
                  {columns.map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>
                      <SkeletonLine count={4} className="my-2" />
                    </td>
                  ))}
                </tr>
              )}
              {isLoading &&
                paginationControls &&
                Array.from({ length: paginationControls.limit }, (_row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((_column, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={index}>
                        <div className="flex h-6 items-center">
                          <SkeletonLine height={16} containerClassName="w-full" />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              {!isLoading && rows && rows?.length > 0 ? (
                <AnimatePresence initial={false}>{rows}</AnimatePresence>
              ) : (
                !isLoading && (
                  <tr>
                    <td colSpan={columns.length}>
                      <div className="my-12 text-center text-14 text-cf-light-3">
                        {emptyRowsContent}
                      </div>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </MacScrollbar>
      </div>
      {paginationControls && (
        <div
          className={classNames(
            'flex flex-col items-center justify-center space-x-4 text-14 md:flex-row md:justify-end',
          )}
        >
          <label className="order-2 text-cf-light-3 md:order-1" htmlFor="pagination">
            Items per page:
            <select
              className="ml-4 cursor-pointer border-b border-cf-gray-5 bg-cf-gray-bg py-2 focus:outline-none"
              id="pagination"
              value={paginationControls.limit}
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                paginationControls.setLimit(Number(event.target.value))
              }
            >
              {paginationControls.paginationLimits.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <div className="order-1 flex items-center md:order-2">
            {!paginationControls.useCursor && showTotalPages && (
              <div
                suppressHydrationWarning
                className={classNames(
                  paginationButtonStyles,
                  paginationControls.pageCounter.atBeginning && disabledStyles,
                )}
                onClick={paginationControls.pageCounter.goToBeginning}
              >
                First
              </div>
            )}

            <PageCounter paginationControls={paginationControls} showTotalPages={showTotalPages} />
            {!paginationControls.useCursor && showTotalPages && (
              <div
                suppressHydrationWarning
                className={classNames(
                  paginationButtonStyles,
                  paginationControls.pageCounter.atEnd && disabledStyles,
                )}
                onClick={paginationControls.pageCounter.goToEnd}
              >
                Last
              </div>
            )}
          </div>

          {exportCsv && (
            <div className="order-3 flex items-center gap-x-2">
              <div className="hidden h-[16px] w-px border-r border-cf-light-1 md:block" />
              <ExportCsvButton onExport={exportCsv} canExport />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
